import type { AreaId, AuditEntryId, PersonId } from '@shared/brand';
import type { AuditKind } from '../vocabulary';
import type { Auditable } from './Organization';

/**
 * An entry in the movement log — the handoff's "Bitácora".
 *
 * Append-only, and not merely by convention: the Firestore rules deny create,
 * update and delete to every client, so the only writer is a Cloud Function
 * using the Admin SDK. That is what makes the log worth having. A log a user can
 * edit is not evidence of anything.
 *
 * Actor and target names are denormalised copies, and here that is not a
 * performance shortcut but a correctness requirement: the log must read the same
 * in five years, after the person has been renamed, moved to another area, or
 * removed. A join would rewrite history.
 */
export interface AuditEntry extends Auditable {
  readonly id: AuditEntryId;
  /**
   * The area the movement belongs to, which scopes who may read it. Entries
   * with company-wide reach are recorded against the sentinel below, and only a
   * superuser sees those.
   */
  readonly areaId: AreaId;
  readonly areaName: string;
  readonly kind: AuditKind;
  readonly actorPersonId: PersonId | null;
  /** Frozen copy — see above. Includes the role suffix the handoff shows,
   *  e.g. "Renato Vidal (RR.HH.)". */
  readonly actorName: string;
  /** One line, past tense: "Aprobó VC-0211 de Bruno Chávez". */
  readonly action: string;
  /** The supporting detail shown under the action. */
  readonly detail: string;
  /** Path of the attached document, or null. Never a signed URL — those expire. */
  readonly documentPath: string | null;
  /** Document this entry refers to, for the "ver detalle" jump. */
  readonly targetPath: string | null;
}

/** Area id used for movements whose scope is the whole company. */
export const GLOBAL_AREA_SENTINEL = '__global__';

export const isGlobalEntry = (entry: Pick<AuditEntry, 'areaId'>): boolean =>
  entry.areaId === GLOBAL_AREA_SENTINEL;

/** Accent colour per kind of movement, from the handoff's timeline. */
export const AUDIT_KIND_COLOR: Readonly<Record<AuditKind, string>> = {
  Solicitud: '#4A6FA5',
  Aprobación: '#12A594',
  Documento: '#7C5CFF',
  Política: '#E8930C',
  Cuenta: '#E5484D',
  Liderazgo: '#5B3FD4',
  Desconocido: '#64748B',
};

/** Glyph per kind. Unicode, matching the design's icon-library-free approach. */
export const AUDIT_KIND_GLYPH: Readonly<Record<AuditKind, string>> = {
  Solicitud: '↗',
  Aprobación: '✓',
  Documento: '▤',
  Política: '§',
  Cuenta: '★',
  Liderazgo: '↺',
  Desconocido: '·',
};

/**
 * Escapes one CSV field: doubles any quote and wraps the value when it contains
 * a delimiter, a quote or a newline. Audit details are free text written by
 * users, so they routinely contain commas and occasionally quotes.
 *
 * The leading-character guard defends against CSV injection: a field starting
 * with `=`, `+`, `-` or `@` is treated as a formula by Excel and Sheets, so an
 * exported log could execute something on the machine of whoever opens it. The
 * value is prefixed with a tab, which neutralises it while staying readable.
 */
export const escapeCsvField = (value: string): string => {
  const neutralized = /^[=+\-@\t\r]/.test(value) ? `\t${value}` : value;
  if (/["\n\r,;]/.test(neutralized)) {
    return `"${neutralized.replace(/"/g, '""')}"`;
  }
  return neutralized;
};
